import { ref, shallowRef, computed } from 'vue'
import { defineStore } from 'pinia'
import authProvider, {
  type LoginPayload,
  type RegisterPayload,
  type CheckDocumentPayload,
  type CheckEmailPayload,
  type SendVerificationCodePayload,
  type VerifyEmailCodePayload,
  type ForgotPasswordPayload,
  type VerifyRecoveryCodePayload,
  type ResetPasswordPayload,
} from '../services/auth_provider'
import type { User } from '../types'
import { clearAuthTokens } from '../services/api'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = shallowRef(false)
  const error = shallowRef<string | null>(null)
  const initialized = shallowRef(false)
  let initPromise: Promise<void> | null = null

  const isAuthenticated = computed(() => !!user.value)

  async function checkDocument(payload: CheckDocumentPayload) {
    loading.value = true
    error.value = null
    try {
      const { data } = await authProvider.checkDocument(payload)
      return data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error al validar documento'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function checkEmail(payload: CheckEmailPayload) {
    loading.value = true
    error.value = null
    try {
      const { data } = await authProvider.checkEmail(payload)
      return data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error al validar correo'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function sendVerificationCode(payload: SendVerificationCodePayload) {
    loading.value = true
    error.value = null
    try {
      const { data } = await authProvider.sendVerificationCode(payload)
      return data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error al enviar código'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function verifyEmailCode(payload: VerifyEmailCodePayload) {
    loading.value = true
    error.value = null
    try {
      const { data } = await authProvider.verifyEmailCode(payload)
      return data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error al validar código'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function forgotPassword(payload: ForgotPasswordPayload) {
    loading.value = true
    error.value = null
    try {
      const { data } = await authProvider.forgotPassword(payload)
      return data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error al solicitar recuperación'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function verifyRecoveryCode(payload: VerifyRecoveryCodePayload) {
    loading.value = true
    error.value = null
    try {
      const { data } = await authProvider.verifyRecoveryCode(payload)
      return data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Código de recuperación inválido'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function resetPassword(payload: ResetPasswordPayload) {
    loading.value = true
    error.value = null
    try {
      const { data } = await authProvider.resetPassword(payload)
      return data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error al actualizar contraseña'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function login(payload: LoginPayload) {
    loading.value = true
    error.value = null
    try {
      const { data } = await authProvider.login(payload)
      user.value = data.user
      initialized.value = true
      if (typeof window !== 'undefined') {
        localStorage.setItem('bera_auth_event', `login:${Date.now()}`)
      }
      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error al iniciar sesión'
      return false
    } finally {
      loading.value = false
    }
  }

  async function register(payload: RegisterPayload) {
    loading.value = true
    error.value = null
    try {
      const { data } = await authProvider.register(payload)
      user.value = data.user
      initialized.value = true
      if (typeof window !== 'undefined') {
        localStorage.setItem('bera_auth_event', `login:${Date.now()}`)
      }
      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error al registrarse'
      return false
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    loading.value = true
    user.value = null
    error.value = null
    try {
      await authProvider.logout()
    } catch {
      // ignore network errors on logout
    } finally {
      user.value = null
      error.value = null
      loading.value = false
      initialized.value = false
      initPromise = null
      clearAuthTokens()

      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('bera_auth_event', `logout:${Date.now()}`)
          for (let index = localStorage.length - 1; index >= 0; index--) {
            const key = localStorage.key(index)
            if (
              key === 'bera_cart' ||
              key?.startsWith('bera_client_profile_') ||
              key?.startsWith('bera_terms_accepted_') ||
              key?.startsWith('bera_accepted_')
            ) {
              localStorage.removeItem(key)
            }
          }
        } catch {
          // ignore
        }
      }
    }
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (event.key === 'bera_auth_event') {
        if (event.newValue?.startsWith('logout:')) {
          user.value = null
          error.value = null
          if (
            !['/login', '/register', '/forgot-password', '/reset-password'].includes(
              window.location.pathname
            )
          ) {
            window.location.href = '/login'
          }
        } else if (event.newValue?.startsWith('login:')) {
          fetchUser()
        }
      }
    })
  }

  async function initialize() {
    if (initialized.value) return
    if (initPromise) return initPromise

    if (
      typeof window !== 'undefined' &&
      ['/login', '/register', '/forgot-password', '/reset-password'].includes(window.location.pathname)
    ) {
      initialized.value = true
      return
    }

    initPromise = (async () => {
      try {
        const { data } = await authProvider.getProfile()
        user.value = data
      } catch (err) {
        console.error('[auth] initialize failed:', err)
        user.value = null
      } finally {
        initialized.value = true
        initPromise = null
      }
    })()

    return initPromise
  }

  async function fetchUser() {
    try {
      const { data } = await authProvider.getProfile()
      user.value = data
    } catch (err) {
      console.error('[auth] fetchUser failed:', err)
      user.value = null
    }
  }

  async function acceptTerms() {
    loading.value = true
    error.value = null
    try {
      const { data } = await authProvider.acceptTerms()
      if (user.value) {
        user.value.terms_accepted_at = data.terms_accepted_at
      }
      return data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Error al aceptar términos'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    user,
    loading,
    error,
    initialized,
    isAuthenticated,
    checkDocument,
    checkEmail,
    sendVerificationCode,
    verifyEmailCode,
    forgotPassword,
    verifyRecoveryCode,
    resetPassword,
    login,
    register,
    logout,
    initialize,
    fetchUser,
    acceptTerms,
  }
})
